import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { InputDollar } from "@/components/_pages/ModelPage/Sidebar/InputDollar";
import { InputDollarPercentSwitch } from "@/components/_pages/ModelPage/Sidebar/InputDollarPercentSwitch";
import { InputPercent } from "@/components/_pages/ModelPage/Sidebar/InputPercent";
import { InputToggle } from "@/components/_pages/ModelPage/Sidebar/InputToggle";
import {
  equityInputDollarAtom,
  equityInvestorEquityStepUpAtom,
  equityInvestorInputDollarAtom,
  equityInvestorPercentAtom,
  equityInvestorPreferredEquityRateAtom,
  equityInvestorStartPrincipalRepaymentsAtom,
  equityPercentAtom,
  equitySearchPhaseCapitalInputDollarAtom,
  equitySellerRollingOverInputDollarAtom,
  equitySellerRollingOverPercentAtom,
  financeStructureErrorMessageAtom,
  financeStructureIsDollarFormatAtom,
  inputEquityInvestorStartPreferredDividendsAtom,
  inputEquityInvestorStartProfitDistributionsAtom,
  inputIsInvestorFundedSearchPhaseAtom,
  isEquityInvestorsIncludedAtom,
  isFinanceStructureInErrorAtom,
  isSb7aLoanTypeAtom,
  isSellerRollingOverEquityAtom,
  sbaLoanInputDollarAtom,
  sbaLoanPercentAtom,
  sellersNoteInputDollarAtom,
  sellersNotePercentAtom,
  targetPurchasePriceAtom,
} from "@/financeModels/owasco/general";
import { numberToPercentInput, percentInputToNumber } from "@/utils/format";
import { lazyReactFallback } from "@/utils/lazy";
import { toNumberOrNull } from "@/utils/math";
import { Alert, Divider, Stack, Text } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import { ChangeEvent } from "react";
import { TbLayoutBoardSplit } from "react-icons/tb";
import { InputYears } from "../../Sidebar/InputYears";

const FinanceStructureSB7aExplainer = lazyReactFallback(
  () => import("@/docs/pages/model/sidebar/financeStructureSB7a.mdx"),
);
const FinanceStructureSellersNoteExplainer = lazyReactFallback(
  () => import("@/docs/pages/model/sidebar/financeStructureSellersNote.mdx"),
);

const FinanceStructureEquityExplainer = lazyReactFallback(
  () => import("@/docs/pages/model/sidebar/financeStructureEquity.mdx"),
);

const FinanceStructureHasEquityInvestorExplainer = lazyReactFallback(
  () =>
    import("@/docs/pages/model/sidebar/financeStructureHasEquityInvestor.mdx"),
);

const FinanceStructureEquityInvestorExplainer = lazyReactFallback(
  () => import("@/docs/pages/model/sidebar/financeStructureEquityInvestor.mdx"),
);

const FinanceStructureEquityInvestorPreferredDividendsExplainer =
  lazyReactFallback(
    () =>
      import(
        "@/docs/pages/model/sidebar/financeStructureEquityInvestorPreferredDividends.mdx"
      ),
  );

const FinanceStructureEquityInvestorPreferredRateExplainer = lazyReactFallback(
  () =>
    import(
      "@/docs/pages/model/sidebar/financeStructureEquityInvestorPreferredRate.mdx"
    ),
);

const FinanceStructureEquityInvestorRepaymentsExplainer = lazyReactFallback(
  () =>
    import(
      "@/docs/pages/model/sidebar/financeStructureEquityInvestorRepayments.mdx"
    ),
);

const FinanceStructureEquityInvestorStepUpExplainer = lazyReactFallback(
  () =>
    import(
      "@/docs/pages/model/sidebar/financeStructureEquityInvestorStepUp.mdx"
    ),
);

const Panel = () => {
  const isSb7aLoanType = useAtomValue(isSb7aLoanTypeAtom);

  const [isInvestorIncluded, setInvestorIncluded] = useAtom(
    isEquityInvestorsIncludedAtom,
  );

  const [isSellerRollingOverEquity, setIsSellerRollingOverEquity] = useAtom(
    isSellerRollingOverEquityAtom,
  );

  const [isDollarFormat, setIsDollarFormat] = useAtom(
    financeStructureIsDollarFormatAtom,
  );

  const [sbaLoanPercent, setSbaLoanPercent] = useAtom(sbaLoanPercentAtom);
  const [sellersNotePercent, setSellersNotePercent] = useAtom(
    sellersNotePercentAtom,
  );
  const [equityPercent, setEquityPercent] = useAtom(equityPercentAtom);
  const [equityInvestorPercent, setEquityInvestorPercent] = useAtom(
    equityInvestorPercentAtom,
  );

  const [equitySellerRollingOverPercent, setEquitySellerRollingOverPercent] =
    useAtom(equitySellerRollingOverPercentAtom);

  const [sbaLoanDollar, setSbaLoanDollar] = useAtom(sbaLoanInputDollarAtom);
  const [sellersNoteDollar, setSellersNoteDollar] = useAtom(
    sellersNoteInputDollarAtom,
  );
  const [equityDollar, setEquityDollar] = useAtom(equityInputDollarAtom);
  const [equityInvestorDollar, setEquityInvestorDollar] = useAtom(
    equityInvestorInputDollarAtom,
  );

  const [
    equitySellerRollingOverInputDollar,
    setEquitySellerRollingOverInputDollar,
  ] = useAtom(equitySellerRollingOverInputDollarAtom);

  const purchasePrice = useAtomValue(targetPurchasePriceAtom);

  const financeStructureErrorMessage = useAtomValue(
    financeStructureErrorMessageAtom,
  );

  const [equityInvestorInterestRate, setEquityInvestorInterestRate] = useAtom(
    equityInvestorPreferredEquityRateAtom,
  );

  const [equityInvestorStepUp, setEquityInvestorStepUp] = useAtom(
    equityInvestorEquityStepUpAtom,
  );

  const [
    equityInvestorStartPrincipalRepayments,
    setEquityInvestorStartPrincipalRepayments,
  ] = useAtom(equityInvestorStartPrincipalRepaymentsAtom);

  const [
    equityInvestorStartPreferredDividends,
    setEquityInvestorStartPreferredDividends,
  ] = useAtom(inputEquityInvestorStartPreferredDividendsAtom);

  const [
    equityInvestorStartProfitDistributions,
    setEquityInvestorStartProfitDistributions,
  ] = useAtom(inputEquityInvestorStartProfitDistributionsAtom);

  const [investorsFundedSearchPhase, setInvestorFundedSearchPhase] = useAtom(
    inputIsInvestorFundedSearchPhaseAtom,
  );

  const [equitySearchPhaseCapitalDollar, setEquitySearchPhaseCapitalDollar] =
    useAtom(equitySearchPhaseCapitalInputDollarAtom);

  const conversionManfest = [
    { type: "dollar", setter: setSbaLoanDollar, value: sbaLoanPercent },
    { type: "dollar", setter: setSellersNoteDollar, value: sellersNotePercent },
    { type: "dollar", setter: setEquityDollar, value: equityPercent },

    { type: "percent", setter: setSbaLoanPercent, value: sbaLoanDollar },
    {
      type: "percent",
      setter: setSellersNotePercent,
      value: sellersNoteDollar,
    },
    { type: "percent", setter: setEquityPercent, value: equityDollar },

    ...(isSellerRollingOverEquity
      ? [
          {
            type: "dollar",
            setter: setEquitySellerRollingOverInputDollar,
            value: equitySellerRollingOverPercent,
          },

          {
            type: "percent",
            setter: setEquitySellerRollingOverPercent,
            value: equitySellerRollingOverInputDollar,
          },
        ]
      : []),

    ...(isInvestorIncluded
      ? [
          {
            type: "dollar",
            setter: setEquityInvestorDollar,
            value: equityInvestorPercent,
          },
          {
            type: "percent",
            setter: setEquityInvestorPercent,
            value: equityInvestorDollar,
          },
        ]
      : []),
  ] as const;

  const onChangeFormat = (value: string) => {
    const isDollarFormat = value === "dollar";
    setIsDollarFormat(isDollarFormat);

    const items = conversionManfest.filter(
      (item) => item.type === (isDollarFormat ? "dollar" : "percent"),
    );

    let amountLeft = Number(isDollarFormat ? purchasePrice : 1);

    for (const item of items) {
      const isLastItem = item === items[items.length - 1];

      const convertedValue = isDollarFormat
        ? Number(item.value) * Number(purchasePrice)
        : Number(item.value) / Number(purchasePrice);

      // If it's the last item, set it to whatever is left to avoid rounding issues
      item.setter(isLastItem ? amountLeft : convertedValue, true);
      amountLeft -= convertedValue;
    }
  };

  return (
    <Stack gap={4}>
      <InputDollarPercentSwitch
        value={isDollarFormat}
        onChange={onChangeFormat}
      />
      {!isDollarFormat ? (
        <InputPercent
          label={
            isSb7aLoanType ? (
              <LabelExplainer
                label="SBA 7(a) Loan"
                explanation={<FinanceStructureSB7aExplainer />}
              />
            ) : (
              <LabelExplainer
                label="Senior Debt"
                explanation={
                  <Doc path="@/docs/pages/model/sidebar/financeStructureSeniorDebt.mdx" />
                }
              />
            )
          }
          value={numberToPercentInput(sbaLoanPercent)}
          onChange={(value) => setSbaLoanPercent(percentInputToNumber(value))}
        />
      ) : (
        <InputDollar
          label={
            isSb7aLoanType ? (
              <LabelExplainer
                label="SBA 7(a) Loan"
                explanation={<FinanceStructureSB7aExplainer />}
              />
            ) : (
              <LabelExplainer
                label="Senior Debt"
                explanation={
                  <Doc path="@/docs/pages/model/sidebar/financeStructureSeniorDebt.mdx" />
                }
              />
            )
          }
          value={Number(sbaLoanDollar)}
          onChange={(value) => setSbaLoanDollar(toNumberOrNull(value))}
        />
      )}
      {!isDollarFormat ? (
        <InputPercent
          label={
            <LabelExplainer
              label="Seller's Note"
              explanation={<FinanceStructureSellersNoteExplainer />}
            />
          }
          value={numberToPercentInput(sellersNotePercent)}
          onChange={(value) =>
            setSellersNotePercent(percentInputToNumber(value))
          }
        />
      ) : (
        <InputDollar
          label={
            <LabelExplainer
              label="Seller's Note"
              explanation={<FinanceStructureSellersNoteExplainer />}
            />
          }
          value={Number(sellersNoteDollar)}
          onChange={(value) => setSellersNoteDollar(toNumberOrNull(value))}
        />
      )}
      {!isDollarFormat ? (
        <InputPercent
          label={
            <LabelExplainer
              label={isInvestorIncluded ? "Equity - Searcher" : "Equity"}
              explanation={<FinanceStructureEquityExplainer />}
            />
          }
          value={numberToPercentInput(equityPercent)}
          onChange={(value) => setEquityPercent(percentInputToNumber(value))}
        />
      ) : (
        <InputDollar
          label={
            <LabelExplainer
              label={isInvestorIncluded ? "Equity - Searcher" : "Equity"}
              explanation={<FinanceStructureEquityExplainer />}
            />
          }
          value={Number(equityDollar)}
          onChange={(value) => setEquityDollar(toNumberOrNull(value))}
        />
      )}

      <InputToggle
        label={
          <LabelExplainer
            label="Seller rolling over equity?"
            explanation={
              <Doc path="@/docs/pages/model/sidebar/financeStructureIsSellerRollingOverEquity.mdx" />
            }
          />
        }
        checked={isSellerRollingOverEquity}
        onChange={(event) => {
          setIsSellerRollingOverEquity(
            (event as ChangeEvent<HTMLInputElement>).currentTarget.checked,
          );
        }}
      />

      {isSellerRollingOverEquity && (
        <>
          {!isDollarFormat ? (
            <InputPercent
              label={
                <LabelExplainer
                  label="Equity - Seller Rollover"
                  explanation={
                    <Doc path="@/docs/pages/model/sidebar/financeStructureEquitySellerRollingOver.mdx" />
                  }
                />
              }
              value={numberToPercentInput(equitySellerRollingOverPercent)}
              onChange={(value) =>
                setEquitySellerRollingOverPercent(percentInputToNumber(value))
              }
            />
          ) : (
            <InputDollar
              label={
                <LabelExplainer
                  label="Equity - Seller Rollover"
                  explanation={
                    <Doc path="@/docs/pages/model/sidebar/financeStructureEquitySellerRollingOver.mdx" />
                  }
                />
              }
              value={Number(equitySellerRollingOverInputDollar)}
              onChange={(value) =>
                setEquitySellerRollingOverInputDollar(toNumberOrNull(value))
              }
            />
          )}
        </>
      )}

      <InputToggle
        label={
          <LabelExplainer
            label="Do you have equity investors?"
            explanation={<FinanceStructureHasEquityInvestorExplainer />}
          />
        }
        checked={isInvestorIncluded}
        onChange={(event) => {
          setInvestorIncluded(
            (event as ChangeEvent<HTMLInputElement>).currentTarget.checked,
          );
        }}
      />

      {isInvestorIncluded && (
        <>
          {!isDollarFormat ? (
            <InputPercent
              label={
                <LabelExplainer
                  label={
                    investorsFundedSearchPhase
                      ? "Equity - Acq. Investors"
                      : "Equity - Investors"
                  }
                  explanation={<FinanceStructureEquityExplainer />}
                />
              }
              value={numberToPercentInput(equityInvestorPercent)}
              onChange={(value) =>
                setEquityInvestorPercent(percentInputToNumber(value))
              }
            />
          ) : (
            <InputDollar
              label={
                <LabelExplainer
                  label={
                    investorsFundedSearchPhase
                      ? "Equity - Acq. Investors"
                      : "Equity - Investors"
                  }
                  explanation={<FinanceStructureEquityInvestorExplainer />}
                />
              }
              value={Number(equityInvestorDollar)}
              onChange={(value) =>
                setEquityInvestorDollar(toNumberOrNull(value))
              }
            />
          )}
        </>
      )}

      {financeStructureErrorMessage && (
        <Alert variant="light" color="red" ta={"center"}>
          {financeStructureErrorMessage}
        </Alert>
      )}

      {(isInvestorIncluded || isSellerRollingOverEquity) && (
        <>
          <Divider my="sm" />
          <Text size="sm" fw={"bold"} mb="sm">
            Equity Investor Details
          </Text>

          <InputPercent
            label={
              <LabelExplainer
                label="Preferred Equity Rate"
                explanation={
                  <FinanceStructureEquityInvestorPreferredRateExplainer />
                }
              />
            }
            value={numberToPercentInput(equityInvestorInterestRate)}
            onChange={(value) =>
              setEquityInvestorInterestRate(percentInputToNumber(value))
            }
          />

          <InputPercent
            rightSection="x"
            label={
              <LabelExplainer
                label="Investor Equity Step-Up"
                explanation={<FinanceStructureEquityInvestorStepUpExplainer />}
              />
            }
            value={toNumberOrNull(equityInvestorStepUp) || ""}
            onChange={(value) => setEquityInvestorStepUp(toNumberOrNull(value))}
          />

          <InputYears
            labelWidth={200}
            value={toNumberOrNull(equityInvestorStartPreferredDividends) || ""}
            onChange={(value) =>
              setEquityInvestorStartPreferredDividends(Number(value))
            }
            label={
              <LabelExplainer
                label="Preferred Dividends Start"
                explanation={
                  <FinanceStructureEquityInvestorPreferredDividendsExplainer />
                }
              />
            }
          />

          <InputYears
            labelWidth={200}
            value={`${
              toNumberOrNull(equityInvestorStartPrincipalRepayments) || ""
            }`}
            onChange={(value) =>
              setEquityInvestorStartPrincipalRepayments(
                toNumberOrNull(Number(value)),
              )
            }
            label={
              <LabelExplainer
                label="Principal Repayments Start"
                explanation={
                  <FinanceStructureEquityInvestorRepaymentsExplainer />
                }
              />
            }
          />

          <InputYears
            labelWidth={200}
            value={`${
              toNumberOrNull(equityInvestorStartProfitDistributions) || ""
            }`}
            onChange={(value) =>
              setEquityInvestorStartProfitDistributions(
                toNumberOrNull(Number(value)),
              )
            }
            label={
              <LabelExplainer
                label="Profit Distributions Start"
                explanation={
                  <Doc path="@/docs/pages/model/sidebar/financeStructureEquityInvestorProfitDistributions.mdx" />
                }
              />
            }
          />

          <InputToggle
            label={
              <LabelExplainer
                label="Investors fund your search phase?"
                explanation={
                  <Doc path="@/docs/pages/model/sidebar/financeStructureInvestorsFundSearchPhase.mdx" />
                }
              />
            }
            checked={investorsFundedSearchPhase}
            onChange={(event) => {
              setInvestorFundedSearchPhase(
                (event as ChangeEvent<HTMLInputElement>).currentTarget.checked,
              );
            }}
          />
          {investorsFundedSearchPhase && (
            <>
              <InputDollar
                label={
                  <LabelExplainer
                    label="Search Phase Investors"
                    explanation={
                      <Doc path="@/docs/pages/model/sidebar/financeStructureSearchPhaseInvestorAmount.mdx" />
                    }
                  />
                }
                value={Number(equitySearchPhaseCapitalDollar || 0)}
                onChange={(value) =>
                  setEquitySearchPhaseCapitalDollar(toNumberOrNull(value))
                }
              />
            </>
          )}
        </>
      )}
    </Stack>
  );
};

export const ModuleFinanceStructure = {
  key: "Finance Structure",
  icon: <TbLayoutBoardSplit />,
  control: "Finance Structure",
  panel: <Panel />,
  errorAtom: isFinanceStructureInErrorAtom,
};
